import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import useFetchUserById from '../hooks/getUsersByID';
import { AtSymbolIcon, BookmarkIcon, CalendarIcon, HomeIcon, StarIcon, UserCircleIcon, UserIcon, UserMinusIcon } from '@heroicons/react/24/solid';
import { handlerSearchGeneder, searchActivitiesFavorite } from '../services/core';
import Badges from '../components/Badges';
import { LogoFull, EmptyDataImage } from '../components/Images';

const UserDetailsPage: React.FC = () => {
	const { id } = useParams();

	const {
		userHook,
		followersHook,
		followingHook,
		experiencesHook
	} = useFetchUserById(id || '');

	const user = useMemo(() => userHook?.data?.[0] || null, [userHook]);
	const experiences = useMemo(() => experiencesHook.data || null, [experiencesHook]);
	const followers = useMemo(() => followersHook.data, [followersHook]);
	const following = useMemo(() => followingHook.data, [followingHook]);

	if (!user) {
    return (<span>Loading Users...</span>);
  }

	return (
		<div className="min-h-screen p-10 mb-20">
			<div className="w-100 block pb-6 border-b-2 border-primary-700">
				<h1 className="font-bold text-2xl">User Details</h1>
			</div>
			<div className="bg-center bg-cover bg-no-repeat hero-bg">
				<div className="container-fixed">
					<div className="flex flex-col items-center gap-2 lg:gap-3.5 py-4 lg:pt-5 lg:pb-10">
						<img className="rounded-full border-3 border-success w-[100px] shrink-0" src={user.photoURL}/>
						<div className="flex items-center gap-1.5">
							<div className="text-lg leading-5 font-semibold text-gray-900">
								{user.name}
							</div>
						</div>
						<div className="flex flex-wrap justify-center gap-1 lg:gap-4.5 text-sm">
							<div className="flex gap-1.25 items-center">
								<AtSymbolIcon className="size-5 text-gray-500"/>

								<span className="text-gray-600 font-medium">
									{user.username}
								</span>
							</div>
							<div className="flex gap-1.25 items-center">
								<UserCircleIcon className="size-5 text-gray-500"/>
								
								<a className="text-gray-600 font-medium hover:text-primary" href={`mailto:${user.email}`}>
									{user.email}
								</a>
							</div>
						</div>
					</div>
				</div>

				<div className="container-fixed">
					<div className="grid grid-cols-1 xl:grid-cols-3 gap-5 lg:gap-7.5">
						<div className="col-span-1">
							<div className="grid gap-5 lg:gap-7.5">
								
								<div className="card">
									<div className="card-body">
										<div className="flex flex-wrap justify-center gap-2 py-1">
										<div className="flex flex-col items-center gap-1.5">
											<span className="text-gray-900 text-2xl lg:text-2.5xl font-semibold">
												{ (followers || [])?.length }
											</span>
											<span className="text-gray-700 text-sm font-normal">
												Followers
											</span>
										</div>
										<span className="sm:ml-8 sm:pl-8 border-l border-l-gray-200">
										</span>
										<div className="flex flex-col items-center gap-1.5">
											<span className="text-gray-900 text-2x lg:text-2.5xl font-semibold">
												{ (following || []).length }
											</span>
											<span className="text-gray-700 text-sm">
												Following
											</span>
										</div>
										</div>
									</div>
								</div>

								<div className="card">
									<div className="card-header">
										<h3 className="card-title">
											About
										</h3>
									</div>
									<div className="card-body">
										<p className="text-sm text-gray-800 leading-5.5 mb-4">
											{user.aboutUser}
										</p>
										<div className="grid gap-y-3.5">
											<div className="flex items-center gap-2.5">
												<CalendarIcon className="size-5 text-gray-500"/>

												<a className="text-sm leading-none text-gray-900 hover:text-primary-active" href="#">
													{user.birthdate}
												</a>
											</div>
											<div className="flex items-center gap-2.5">
												<StarIcon className="size-5 text-gray-500"/>

												<a className="text-sm leading-none text-gray-900 hover:text-primary-active" href="#">
													{user.rating || 3}
												</a>
											</div>
											<div className="flex items-center gap-2.5">
												<UserIcon className="size-5 text-gray-500"/>

												<a className="text-sm leading-none text-gray-900 hover:text-primary-active" href="#">
													{handlerSearchGeneder(user?.gender || '')}
												</a>
											</div>
											<div className="flex items-center gap-2.5">
												<AtSymbolIcon className="size-5 text-gray-500"/>
												<a className="text-sm leading-none text-gray-900 hover:text-primary-active" href="#">
													{user.email}
												</a>
											</div>
											<div className="flex items-center gap-2.5">
												<BookmarkIcon className="size-5 text-gray-500"/>

												<a className="text-sm leading-none text-gray-900 hover:text-primary-active" href="#">
													{ searchActivitiesFavorite(user.activities || []) }
												</a>
											</div>

											<div className="flex items-center gap-2.5">
												<HomeIcon className="size-5 text-gray-500"/>
												<a className="text-sm leading-none text-gray-900 hover:text-primary-active text-center" href="#">
													{ user.zipcode }
												</a>
											</div>

											{ user.badge && <div className="flex items-center justify-center gap-2.5">
													<a className="text-sm text-center leading-none text-gray-900 hover:text-primary-active" href="#">
														<Badges badge={user.badge || ''} styles={{ width: '100px', height: '100px' }}/>
													</a>
												</div>
											}
										</div>
									</div>
								</div>
								
							</div>
						</div>

						<div className="col-span-2">
							<h1 className='text-2xl font-bold text-gray-800 mt-1 mb-4'>My Experiences</h1>

							{ (experiences == null || !experiences.length) && 
								<div className="w-full block justify-center text-center align-center mt-4">
									<img className="w-5/12 max-w-xl mr-auto ml-auto" src={EmptyDataImage} alt="Empty Data"/>
									<div className="text-center text-gray-700 text-lg font-medium mt-5">
										No Experiences Found
									</div>
								</div>
							}

										
							<div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5 lg:gap-7.5">
								{ experiences != null && <>
										{ experiences.map((experience: any) => (
												<div className="card p-7" key={experience.id}>
													<div className="flex items-center justify-between mb-3 lg:mb-6">
															<div className="flex items-center justify-center size-[50px] rounded-lg bg-gray-100 bg-primary p-1">
																	<img alt="" className="" src={LogoFull} />
															</div>
															{ experience.isActivityOpen && <span className="badge badge-success badge-outline">Open</span>}
															{ !experience.isActivityOpen && <span className="badge badge-danger badge-outline">Closed</span>}
													</div>
													<div className="flex flex-col mb-3 lg:mb-6">
															<a className="text-lg font-media/brand text-gray-900 hover:text-primary-active mb-px" href="#">
																{experience.title}
															</a>
															<span className="text-sm text-gray-700">
																{experience.description}
															</span>
													</div>
													<div className="flex items-center gap-5 mb-3.5 lg:mb-7">
															<span className="text-sm text-gray-600">
																	Start:
																	<span className="text-sm font-medium text-gray-800 ml-1">
																		{ new Date(experience.date).toLocaleDateString() }
																	</span>
															</span>
															<span className="text-sm text-gray-600">
																	Visibility:
																	<span className="text-sm font-medium text-gray-800 ml-1">
																		{ experience.visibility && 'Publico'}
																		{ !experience.visibility && 'Privado'}
																	</span>
															</span>
													</div>
													<div className="flex items-center justify-center flex-wrap gap-2 lg:gap-5">
														<div className="flex flex-col gap-1.5 border border-dashed border-gray-300 rounded-md px-2.5 py-2">
															<span className="text-gray-900 text-sm leading-none font-medium">
																{ searchActivitiesFavorite(experience.experienceType || '') }
															</span>
															<span className="text-gray-700 text-xs">
																Experience Type
															</span>
														</div>
														<div className="flex flex-col gap-1.5 border border-dashed border-gray-300 rounded-md px-2.5 py-2">
															<span className="text-gray-900 text-sm leading-none font-medium">
																{ experience.participants }
															</span>
															<span className="text-gray-700 text-xs">
																Participants
															</span>
														</div>
													</div>
												</div>
											))
										}
									</>
								}
							</div>

						</div>

					</div>
				</div>
     	</div>
		</div>
	);
};

export default UserDetailsPage;